import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { Button, Input, Spinner } from '@pijma/crypto'
import { IconHelp } from '@pijma/icon-gallery/crypto'
import { useNumericMask } from '@pijma/input'
import { SourceInfo } from '@pijma/input/lib/dts/hooks/utils/types'

import { TFailureResponse } from '@api/types'
import { useToken } from '@hooks'
import { Dispatch, RootState } from '@stores'
import { TPayFormState } from '@stores/types/TPayFormState'
import { localeNumber, removeLeadingZeroes } from '@utils'
import { HttpStatusCode } from 'axios'
import styled from 'styled-components'

import { Color } from '../../../../../generated/quicktype/Tokens'
import { shopApplicationClient } from '../../../../api/clients/shop-application-client'
import { EOperationErrorCause } from '../../../../api/types/EOperationErrorCause'
import { appClose } from '../../../../helpers/route-utils'
import { isWebview } from '../../../../helpers/webview-utils'
import { EShop } from '../../../../stores/types/EShop'
import { H3Weight600 } from '../../../common/header/Headings'
import { Island } from '../../../common/island'
import { FlexBoxCenteredLayout } from '../../../common/layout/flex-box'
import { BodyText600, CaptionText500 } from '../../../common/text/BodyText'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: 0 20px;
`

const LoginContainer = styled.div`
  display: flex;
  width: 100%;
`

const LoginButtonWithHelpWrapper = styled.div`
  display: flex;
  margin: 20px;
  flex-direction: column;
`

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 32px 0 0;
`

const LoginHelpContainer = styled.div`
  margin: 8px 0 0 8px;
  cursor: pointer;
  width: fit-content;
`

const PaymentIslandWrapper = styled.div`
  padding: 8px 0 0 0;
  width: 100%;
`

const PaymentFiatWrapper = styled.div`
  padding: 20px 20px 0;
  width: 100%;
`

const PaymentCryptoWrapper = styled.div`
  padding: 24px 20px 20px;
  width: 100%;
`

const Header = styled.div<Color>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  background-color: ${(color) => color.bg.page.value};
  padding-left: 4px;
`

const IslandBlock = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  margin-bottom: 24px;
  font-weight: 600;
  flex-direction: column;
`

const SngBlock = styled.div`
  display: flex;
  width: fit-content;
  align-items: center;
  margin: 8px 0 0 0;
  cursor: pointer;
`

const CloseContainer = styled.div`
  margin: 24px 20px 0 20px;
  color: #217aac;

  span {
    cursor: pointer;

    :hover {
      color: rgb(49, 49, 49);
    }
  }
`

interface StateProps {
  payForm: TPayFormState
  fee: number
}

interface DispatchProps {
  setAmount: (amountTo: string) => void
  setAccount: (account: string) => void
  doPaymentBlocking: () => void
  resetPaymentBlocking: () => void
}

type Props = StateProps & DispatchProps

const trueAmount = (formattedAmount: string) => {
  return formattedAmount.replace(',', '.').replaceAll(' ', '')
}

// Только сумму с запятой можно вставлять в поле для дальнейшего форматирования. Точка теряется.
// Костыль, но лучше не придумал, да и вроде как пофиг
const fixableAmount = (formattedAmount: string | undefined) => {
  if (formattedAmount == undefined) return ''
  return formattedAmount
    .replace('.', ',')
    .replaceAll(' ', '')
    .replace(/(\d+),(\d\d)\d*/g, '$1,$2')
}

const convertWithFee = (amount: string, fee: number) => {
  return fixableAmount(String(+amount * (1 + fee)))
}

const reverseConvertWithFee = (amount: string, fee: number) => {
  return fixableAmount(String(+amount / (1 + fee)))
}

const SteamPage = (props: Props) => {
  const intl = useIntl()
  const redirectTo = useNavigate()

  const [steamLogin, setSteamLogin] = useState('')
  const [prettyAmountUsdt, setPrettyAmountUsdt] = useState('')
  const [prettyAmountFiat, setPrettyAmountFiat] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [loginErrorMessage, setLoginErrorMessage] = useState('')
  const [loginOk, setLoginOk] = useState(false)

  useEffect(() => {
    document.title = '\u200B'
    const amount = +(props.payForm.amountTo as string)
    const amountOutsideLimits =
      amount &&
      (amount < +(props.payForm.minLimit as string) ||
        amount > +(props.payForm.maxLimit as string))
    const incorrectAmount = Number.isNaN(amount)

    if (amountOutsideLimits) {
      setErrorMessage(
        `${intl.formatMessage({
          id: 'shopSteamPage.amountOutsideLimits',
        })} ${localeNumber(props.payForm.minLimit)} ${intl.formatMessage({
          id: 'shopSteamPage.to',
        })} ${localeNumber(props.payForm.maxLimit)} ${intl.formatMessage({
          id: 'shopSteamPage.usd',
        })}`,
      )
    } else if (incorrectAmount) {
      setErrorMessage(
        intl.formatMessage({ id: 'shopSteamPage.notLooksLikeAmount' }),
      )
    } else {
      setErrorMessage('')
    }
  }, [
    props.payForm.amountTo,
    props.payForm.maxLimit,
    props.payForm.minLimit,
    props.payForm.application?.currency,
  ])

  const maskSteamLogin = {
    onChange: (e: { target: { value: string } }) => {
      setLoginOk(false)
      setSteamLogin(() => e.target.value)
    },
  }

  const checkAccount = (account: string) => {
    if (account === '') {
      setLoginErrorMessage('')
      setLoginOk(false)
      return
    }
    const ifLoginLooksReal = /^\w{3,44}$/.test(account)
    if (!ifLoginLooksReal) {
      setLoginErrorMessage(
        intl.formatMessage({ id: 'shopSteamPage.notLooksLikeAccount' }),
      )
      setLoginOk(false)
      return
    }
    shopApplicationClient
      .checkAccount(EShop.STEAM, account)
      .then(() => {
        setLoginErrorMessage('')
        setLoginOk(true)
        props.setAccount(account)
      })
      .catch((error) => {
        setLoginOk(false)
        if (error.response.status == HttpStatusCode.NotFound) {
          setLoginErrorMessage(
            intl.formatMessage({ id: 'shopSteamPage.notSngAccount' }),
          )
        } else {
          setLoginErrorMessage(
            intl.formatMessage({ id: 'shopSteamPage.couldNotCheckAccount' }),
          )
        }
      })
  }

  const pay = () => {
    if (!props.payForm.application?.applicationUuid) return
    if (!props.payForm.account) return
    if (!props.payForm.amountTo) return
    if (!props.payForm.paymentGateway?.paymentGatewayId) return
    props.doPaymentBlocking()
    shopApplicationClient
      .payment(
        props.payForm.application.applicationUuid,
        props.payForm.account,
        props.payForm.amountTo,
        props.payForm.paymentGateway.paymentGatewayId,
      )
      .then(() => redirectTo('../pending'))
      .catch((error) => {
        props.resetPaymentBlocking()
        const err = error.response.data as TFailureResponse
        // eslint-disable-next-line sonarjs/no-small-switch
        switch (err.errorCode) {
          case EOperationErrorCause.INSUFFICIENT_FUNDS:
            redirectTo('../insufficient-funds')
            return
          default:
            redirectTo('../failure')
            return
        }
      })
  }

  const maskPropsFiat = useNumericMask({
    value: prettyAmountFiat,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amount = trueAmount(formattedValue)
        props.setAmount(amount)
        setPrettyAmountUsdt(convertWithFee(amount, props.fee))
      }
      setPrettyAmountFiat(formattedValue)
    },
  })

  const maskPropsUsdt = useNumericMask({
    value: prettyAmountUsdt,
    decimalScale: 2,
    allowedDecimalSeparators: ['%', '.', ',', 'б', 'ю'],
    decimalSeparator: ',',
    thousandsGroupStyle: 'thousand',
    thousandSeparator: '',
    staticSeparator: true,
    allowLeadingZeros: false,
    allowNegative: false,
    onValueChange: ({ formattedValue }, sourceInfo: SourceInfo) => {
      formattedValue = removeLeadingZeroes(formattedValue)
      if (sourceInfo.event) {
        const amountFiat = reverseConvertWithFee(
          trueAmount(formattedValue),
          props.fee,
        )
        props.setAmount(trueAmount(amountFiat))
        setPrettyAmountFiat(amountFiat)
      }
      setPrettyAmountUsdt(formattedValue)
    },
  })

  const percentageFee = ((props.payForm.fee || 0) * 100).toFixed(0)
  const tokens = useToken()

  useEffect(
    () => {
      setSteamLogin(props.payForm.account || '')
      checkAccount(props.payForm.account || '')
    }, // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.payForm.account],
  )

  // should call once, if component is mounted
  useEffect(
    () => {
      if (props.payForm.amountTo !== '0') {
        setPrettyAmountFiat(props.payForm.amountTo || '')
        setPrettyAmountUsdt(
          convertWithFee(props.payForm.amountTo || '', props.fee),
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  return (
    <FlexBoxCenteredLayout>
      <Container>
        <Header {...tokens.color}>
          <IslandBlock>
            <H3Weight600>
              {intl.formatMessage({ id: 'shopSteamPage.title' })}
            </H3Weight600>
            <SngBlock onClick={() => redirectTo('help_sng_list')}>
              <img
                src={`${process.env.PUBLIC_URL}/img/sng_logo.png`}
                width={'24px'}
                height={'24px'}
                style={{ borderRadius: '50%', marginRight: '8px' }}
              />
              <BodyText600>
                {intl.formatMessage({ id: 'shopSteamPage.sngCountries' })}
              </BodyText600>
              <IconHelp
                width={'20px'}
                height={'20px'}
                style={{ marginLeft: '4px' }}
              />
            </SngBlock>
          </IslandBlock>
        </Header>
        <Island size="M" bgColor="primary">
          <LoginButtonWithHelpWrapper>
            <LoginContainer>
              <Input
                onChange={maskSteamLogin.onChange}
                onBlur={(e) => checkAccount(e.target.value)}
                label={`${intl.formatMessage({
                  id: 'shopSteamPage.steamLogin',
                })}`}
                value={steamLogin}
                width={'100%'}
                errorMessage={loginErrorMessage}
                invalid={loginErrorMessage != ''}
                disabled={props.payForm.paymentBlocking}
                type={'text'}
                allowClear={true}
              />
            </LoginContainer>
            {!loginErrorMessage && (
              <LoginHelpContainer onClick={() => redirectTo('help_login')}>
                <CaptionText500 color={tokens.color.font.link.default.value}>
                  {intl.formatMessage({ id: 'shopSteamPage.whereToFind' })}
                </CaptionText500>
              </LoginHelpContainer>
            )}
          </LoginButtonWithHelpWrapper>
        </Island>
        <PaymentIslandWrapper>
          <Island size="M" bgColor="primary">
            <PaymentFiatWrapper>
              <Input
                {...maskPropsFiat}
                label={`${intl.formatMessage({
                  id: 'shopSteamPage.amount',
                })}, $`}
                value={prettyAmountFiat}
                width={'100%'}
                description={
                  errorMessage ||
                  `${props.payForm.minLimit} – ${props.payForm.maxLimit} $`
                }
                invalid={errorMessage != ''}
                disabled={props.payForm.paymentBlocking}
                type={'text'}
                inputMode={'decimal'}
                placeholder={'0'}
                allowClear={true}
              />
            </PaymentFiatWrapper>
            <PaymentCryptoWrapper>
              <Input
                {...maskPropsUsdt}
                label={`${intl.formatMessage({
                  id: 'shopSteamPage.payInUsdt',
                })}`}
                value={prettyAmountUsdt}
                width={'100%'}
                description={`${intl.formatMessage({
                  id: 'shopSteamPage.fee',
                })} ${percentageFee}%`}
                disabled={props.payForm.paymentBlocking}
                type={'text'}
                inputMode={'decimal'}
                placeholder={'0'}
                allowClear={true}
              />
            </PaymentCryptoWrapper>
          </Island>
          <ButtonContainer data-testid="amount-page.button.forward">
            <Button
              width={'100%'}
              size={'m'}
              disabled={
                props.payForm.paymentBlocking ||
                !+(props.payForm.amountTo as string) ||
                errorMessage != '' ||
                loginErrorMessage != '' ||
                !steamLogin ||
                !loginOk
              }
              onPress={() => pay()}
            >
              {props.payForm.paymentBlocking ? (
                <Spinner size={28} color={'#fff'} />
              ) : (
                intl.formatMessage({ id: 'shopSteamPage.pay' })
              )}
            </Button>
          </ButtonContainer>
        </PaymentIslandWrapper>
        {!isWebview(navigator.userAgent) && (
          <CloseContainer onClick={() => appClose()}>
            <BodyText600>
              {props.payForm.application?.product
                ? intl.formatMessage(
                    { id: 'shopSteamPage.back' },
                    { product: props.payForm.application.product },
                  )
                : null}
            </BodyText600>
          </CloseContainer>
        )}
      </Container>
    </FlexBoxCenteredLayout>
  )
}

const mapStateToProps = (state: RootState): StateProps => ({
  payForm: state.payForm,
  fee: state.payForm?.fee as number,
})

const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => {
  return {
    setAmount: (amountTo: string) =>
      dispatch.payForm.set({
        amountTo,
      }),
    setAccount: (account: string) =>
      dispatch.payForm.set({
        account,
      }),
    doPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: true,
      }),
    resetPaymentBlocking: () =>
      dispatch.payForm.set({
        paymentBlocking: false,
      }),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SteamPage)
